import { useState, useRef, useCallback, useEffect } from "react";

/**
 * Before/After slider — drag the vertical handle horizontally to reveal
 * the "before" (generic SME ad) vs "after" (Ads Creator) versions.
 * Pure SVG, zero deps.
 */
const W = 1200, H = 628;

const PAIRS = [
  {
    id: "toprankpulse",
    label: "TopRankPulse",
    before: <BeforeTopRank />,
    after: <AfterTopRank />,
    metrics: { before: { ctr: 0.7, cpc: 0.84, cvr: 0.9 }, after: { ctr: 2.4, cpc: 0.29, cvr: 3.8 } },
  },
  {
    id: "stavebne-dopyty",
    label: "Stavebné Dopyty",
    before: <BeforeStavebne />,
    after: <AfterStavebne />,
    metrics: { before: { ctr: 0.5, cpc: 1.10, cvr: 0.6 }, after: { ctr: 1.9, cpc: 0.42, cvr: 2.9 } },
  },
  {
    id: "martinus",
    label: "Martinus",
    before: <BeforeMartinus />,
    after: <AfterMartinus />,
    metrics: { before: { ctr: 0.9, cpc: 0.58, cvr: 1.2 }, after: { ctr: 3.1, cpc: 0.21, cvr: 4.2 } },
  },
];

export default function BeforeAfterSection() {
  const [active, setActive] = useState(0);
  const cur = PAIRS[active];
  return (
    <section className="ads-section" data-testid="ads-before-after" style={{ background: "var(--ads-surface)" }}>
      <div className="ads-container">
        <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 8, flexWrap: "wrap" }}>
          <span className="ads-eyebrow">BEFORE · AFTER</span>
          <span style={{ color: "var(--ads-text-dim)", fontSize: 13 }}>Drag the handle ←→ to reveal the difference.</span>
        </div>
        <h2 className="ads-section-title">What most SMEs ship. What you ship with Ads Creator.</h2>

        <div className="ads-ba-tabs" data-testid="ads-ba-tabs">
          {PAIRS.map((p, i) => (
            <button key={p.id}
              className={`ads-chip ${i === active ? "active" : ""}`}
              onClick={() => setActive(i)}
              data-testid={`ads-ba-tab-${p.id}`}
            >
              {p.label}
            </button>
          ))}
        </div>

        <Slider key={cur.id} before={cur.before} after={cur.after} metrics={cur.metrics} />

        <div className="ads-ba-legend">
          <div className="ads-ba-legend-item"><span className="ads-ba-dot ads-ba-dot-before" /> BEFORE · generic Canva template</div>
          <div className="ads-ba-legend-item"><span className="ads-ba-dot ads-ba-dot-after" /> AFTER · Ads Creator output</div>
        </div>
      </div>
    </section>
  );
}

function Slider({ before, after, metrics }) {
  const [pos, setPos] = useState(52); // percent
  const wrapRef = useRef(null);
  const dragging = useRef(false);

  // Interpolate live metrics from BEFORE → AFTER
  const mix = pos / 100;
  const lerp = (a, b) => a * (1 - mix) + b * mix;
  const curMetrics = {
    ctr: lerp(metrics.before.ctr, metrics.after.ctr).toFixed(2),
    cpc: lerp(metrics.before.cpc, metrics.after.cpc).toFixed(2),
    cvr: lerp(metrics.before.cvr, metrics.after.cvr).toFixed(1),
  };

  const onMove = useCallback((clientX) => {
    const el = wrapRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const x = Math.min(Math.max(clientX - r.left, 0), r.width);
    setPos((x / r.width) * 100);
  }, []);

  useEffect(() => {
    function up() { dragging.current = false; }
    function move(e) {
      if (!dragging.current) return;
      const cx = e.touches ? e.touches[0].clientX : e.clientX;
      onMove(cx);
    }
    window.addEventListener("mouseup", up);
    window.addEventListener("touchend", up);
    window.addEventListener("mousemove", move);
    window.addEventListener("touchmove", move, { passive: true });
    return () => {
      window.removeEventListener("mouseup", up);
      window.removeEventListener("touchend", up);
      window.removeEventListener("mousemove", move);
      window.removeEventListener("touchmove", move);
    };
  }, [onMove]);

  function startDrag(e) {
    dragging.current = true;
    const cx = e.touches ? e.touches[0].clientX : e.clientX;
    onMove(cx);
  }

  return (
    <div
      ref={wrapRef}
      className="ads-ba-wrap"
      data-testid="ads-ba-slider"
      onMouseDown={startDrag}
      onTouchStart={startDrag}
      style={{
        position: "relative",
        width: "100%",
        aspectRatio: `${W} / ${H}`,
        borderRadius: 16,
        overflow: "hidden",
        cursor: "ew-resize",
        userSelect: "none",
        touchAction: "none",
        boxShadow: "0 30px 80px rgba(0,0,0,0.55)",
        background: "#000",
      }}
    >
      {/* AFTER is the base layer (always rendered); BEFORE sits on top and is clipped
          from the right as the handle moves right — revealing AFTER on the right.
          At pos=0 → BEFORE fully covers, at pos=100 → BEFORE fully clipped. */}
      <div style={{ position: "absolute", inset: 0 }}>{after}</div>
      <div style={{
        position: "absolute", inset: 0,
        clipPath: `inset(0 ${pos}% 0 0)`,
      }}>{before}</div>

      {/* Labels */}
      <div className="ads-ba-tag ads-ba-tag-before" data-testid="ads-ba-tag-before">
        Before
      </div>
      <div className="ads-ba-tag ads-ba-tag-after" data-testid="ads-ba-tag-after">
        After · Ads Creator
      </div>

      {/* Live metrics overlay — interpolates as the handle moves */}
      <div className="ads-ba-metrics" data-testid="ads-ba-metrics">
        <div className="ads-ba-metric">
          <div className="ads-ba-metric-label">CTR</div>
          <div className="ads-ba-metric-value">{curMetrics.ctr}%</div>
        </div>
        <div className="ads-ba-metric">
          <div className="ads-ba-metric-label">CPC</div>
          <div className="ads-ba-metric-value">€{curMetrics.cpc}</div>
        </div>
        <div className="ads-ba-metric">
          <div className="ads-ba-metric-label">CVR</div>
          <div className="ads-ba-metric-value">{curMetrics.cvr}%</div>
        </div>
      </div>

      {/* Handle */}
      <div className="ads-ba-handle" style={{ left: `${pos}%` }} data-testid="ads-ba-handle">
        <div className="ads-ba-handle-line" />
        <div className="ads-ba-handle-knob">
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="15 18 9 12 15 6" />
            <polyline points="9 18 15 12 9 6" transform="translate(-2 0)" />
          </svg>
        </div>
      </div>
    </div>
  );
}

/* ===================== SVG CANVASES ===================== */

function Frame({ children, bg }) {
  return (
    <svg viewBox={`0 0 ${W} ${H}`} width="100%" height="100%"
         preserveAspectRatio="xMidYMid slice"
         style={{ display: "block" }}
         xmlns="http://www.w3.org/2000/svg">
      <rect x="0" y="0" width={W} height={H} fill={bg} />
      {children}
    </svg>
  );
}

/* ---------- TopRankPulse ---------- */
function BeforeTopRank() {
  // Generic "SEO services" ad: stock-photo vibes (faked with gradient),
  // cluttered text, random colors, watermark placeholder.
  return (
    <Frame bg="#E8EEF5">
      {/* Fake stock-photo blob */}
      <defs>
        <linearGradient id="stockA" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="#B7C4D6" />
          <stop offset="1" stopColor="#8395B0" />
        </linearGradient>
      </defs>
      <rect x="60" y="60" width={W - 120} height={H - 120} fill="url(#stockA)" />
      {/* Random yellow "SALE" starburst */}
      <g transform={`translate(${W - 230}, 110)`}>
        <circle r="90" fill="#FFD93D" />
        <text x="0" y="-5" textAnchor="middle" fontSize="34" fontWeight="800"
              fontFamily="Arial, sans-serif" fill="#B00020">HOT!</text>
        <text x="0" y="30" textAnchor="middle" fontSize="18" fontWeight="700"
              fontFamily="Arial, sans-serif" fill="#1E1E1E">LIMITED</text>
      </g>
      {/* Clip-art globe */}
      <g transform="translate(170, 330)">
        <circle r="70" fill="#3A83F1" />
        <ellipse rx="70" ry="28" fill="none" stroke="#fff" strokeWidth="3" />
        <path d="M-70 0 Q 0 -90 70 0" fill="none" stroke="#fff" strokeWidth="3" />
        <path d="M-70 0 Q 0 90 70 0" fill="none" stroke="#fff" strokeWidth="3" />
      </g>
      {/* Bad headline (Arial, mixed sizes, two colors) */}
      <text x="280" y="230" fontFamily="Arial, sans-serif" fontSize="54" fontWeight="700" fill="#0B3A73">
        Professional SEO
      </text>
      <text x="280" y="290" fontFamily="Arial, sans-serif" fontSize="42" fontWeight="400" fill="#B00020">
        Services for Your Business!
      </text>
      <text x="280" y="350" fontFamily="Comic Sans MS, Comic Sans, cursive" fontSize="22" fill="#1E1E1E">
        ★ Best Price ★ Fast Delivery ★ Guaranteed Results ★
      </text>
      {/* Bullet list */}
      <text x="280" y="400" fontFamily="Arial, sans-serif" fontSize="18" fill="#1E1E1E">
        • Keyword research  • Backlink building  • Content writing
      </text>
      <text x="280" y="425" fontFamily="Arial, sans-serif" fontSize="18" fill="#1E1E1E">
        • On-page SEO  • Monthly reports  • 15+ years experience
      </text>
      {/* Generic CTA */}
      <rect x="280" y="470" width="200" height="48" fill="#3A83F1" />
      <text x="380" y="501" textAnchor="middle" fontFamily="Arial, sans-serif"
            fontSize="18" fontWeight="700" fill="#FFFFFF">Contact Us Now</text>
      {/* Phone + email clutter */}
      <text x="510" y="500" fontFamily="Arial, sans-serif" fontSize="16" fill="#1E1E1E">
        📞 +421 900 123 456
      </text>
      <text x="510" y="522" fontFamily="Arial, sans-serif" fontSize="16" fill="#1E1E1E">
        ✉ info@seo-services.eu
      </text>
      {/* Watermark */}
      <text x={W - 150} y={H - 40} fontFamily="Arial" fontSize="11" fill="#6B7A92" opacity="0.7">
        stock photo · shutterstock
      </text>
    </Frame>
  );
}

function AfterTopRank() {
  // The hero ad from LivePreview.
  return (
    <Frame bg="#0B0B22">
      <circle cx={820 + 340} cy={-120 + 340} r={340} fill="#A855F7" opacity="0.16" />
      <circle cx={920 + 220} cy={-20 + 220} r={220} fill="#A855F7" opacity="0.22" />
      <rect x={60} y={64} width={12} height={14} fill="#A855F7" rx="2" />
      <text x={82} y={78} fill="#A855F7" fontSize="18" fontWeight="700"
            fontFamily="Inter, system-ui, sans-serif" letterSpacing="2">AI VISIBILITY</text>
      <text x={60} y={200} fill="#FFFFFF" fontSize="78" fontWeight="900"
            fontFamily="Inter, system-ui, sans-serif">73% of websites</text>
      <text x={60} y={288} fill="#FFFFFF" fontSize="78" fontWeight="900"
            fontFamily="Inter, system-ui, sans-serif">are invisible to AI.</text>
      <text x={60} y={410} fill="#A5A3C9" fontSize="24"
            fontFamily="Inter, system-ui, sans-serif">
        ChatGPT recommends your competitor — not you.
      </text>
      <text x={60} y={444} fill="#A5A3C9" fontSize="24"
            fontFamily="Inter, system-ui, sans-serif">
        Free 30-second scan, no card.
      </text>
      <rect x={60} y={H - 130} width={260} height={68} rx="34" fill="#A855F7" />
      <text x={190} y={H - 86} fill="#0B0B22" fontSize="26" fontWeight="800"
            fontFamily="Inter, system-ui, sans-serif" textAnchor="middle">Scan my site</text>
      <text x={340} y={H - 86} fill="#A5A3C9" opacity="0.9" fontSize="20" fontWeight="600"
            fontFamily="Inter, system-ui, sans-serif">toprankpulse.com</text>
    </Frame>
  );
}

/* ---------- Stavebné Dopyty ---------- */
function BeforeStavebne() {
  return (
    <Frame bg="#FFFACD">
      {/* Wild yellow background with "construction" texture via stripes */}
      <g opacity="0.35">
        {Array.from({ length: 30 }).map((_, i) => (
          <rect key={i} x={i * 45 - 100} y="-20" width="20" height={H + 40}
                fill={i % 2 === 0 ? "#1E1E1E" : "#FFD93D"} transform="rotate(-12)" />
        ))}
      </g>
      {/* Orange burst */}
      <g transform={`translate(${W - 190}, 150)`}>
        <polygon points="0,-120 20,-30 120,-30 40,20 70,110 0,50 -70,110 -40,20 -120,-30 -20,-30"
                 fill="#E34700" />
        <text x="0" y="0" textAnchor="middle" fontSize="30" fontWeight="800"
              fontFamily="Arial" fill="#FFFFFF">AKCIA!</text>
        <text x="0" y="30" textAnchor="middle" fontSize="16" fontWeight="700"
              fontFamily="Arial" fill="#FFFFFF">LEN DNES</text>
      </g>
      {/* Crane clipart */}
      <g transform="translate(90, 380)" stroke="#1E1E1E" strokeWidth="4" fill="#FFD93D">
        <rect x="-6" y="-150" width="12" height="230" />
        <line x1="0" y1="-150" x2="150" y2="-150" />
        <line x1="120" y1="-150" x2="120" y2="-110" />
        <rect x="112" y="-110" width="16" height="16" fill="#1E1E1E" />
      </g>
      {/* Very bad typography */}
      <text x="230" y="160" fontFamily="Impact, Charcoal, sans-serif" fontSize="66"
            fontWeight="900" fill="#E34700" stroke="#1E1E1E" strokeWidth="3">
        POTREBUJETE
      </text>
      <text x="230" y="230" fontFamily="Impact, sans-serif" fontSize="66"
            fontWeight="900" fill="#1E1E1E">
        STAVEBNÚ FIRMU???
      </text>
      <text x="230" y="290" fontFamily="Comic Sans MS, Comic Sans, cursive"
            fontSize="26" fill="#0066CC">
        Zavolajte ešte dnes na naše čísla!!!
      </text>
      <text x="230" y="340" fontFamily="Arial" fontSize="22" fill="#1E1E1E">
        ☎ 0900 111 222  ☎ 0905 333 444  ☎ 0918 555 666
      </text>
      <text x="230" y="372" fontFamily="Arial" fontSize="18" fill="#1E1E1E">
        Murovanie, omietky, strechy, okná, dvere, kúrenie, sanita, elektro…
      </text>
      <text x="230" y="400" fontFamily="Arial" fontSize="18" fill="#1E1E1E">
        Rýchlo — Lacno — Kvalitne — Bratislava a okolie — 30% zľava do 31.12!
      </text>
      {/* Rainbow CTA */}
      <rect x="230" y="450" width="320" height="60" fill="#00AA44" stroke="#1E1E1E" strokeWidth="4" />
      <text x="390" y="490" textAnchor="middle" fontFamily="Impact, sans-serif"
            fontSize="30" fontWeight="900" fill="#FFFFFF">ZAVOLAJTE HNEĎ!</text>
    </Frame>
  );
}

function AfterStavebne() {
  return (
    <Frame bg="#0F1A14">
      {Array.from({ length: 6 }).map((_, i) => (
        <rect key={i} x={720 + i * 60} y={-20} width={24} height={700}
              fill="#F59E0B" opacity={0.08 + i * 0.04}
              transform={`rotate(12 ${720 + i * 60 + 12} 330)`} />
      ))}
      <rect x={60} y={64} width={12} height={14} fill="#F59E0B" rx="2" />
      <text x={82} y={78} fill="#F59E0B" fontSize="18" fontWeight="700"
            fontFamily="Inter, system-ui, sans-serif" letterSpacing="2">
        STAVEBNÍCTVO · SK
      </text>
      <text x={60} y={180} fill="#FFFFFF" fontSize="76" fontWeight="900"
            fontFamily="Inter, system-ui, sans-serif">Staviate dom?</text>
      <text x={60} y={260} fill="#FFFFFF" fontSize="76" fontWeight="900"
            fontFamily="Inter, system-ui, sans-serif">3 cenové ponuky</text>
      <text x={60} y={340} fill="#FFFFFF" fontSize="76" fontWeight="900"
            fontFamily="Inter, system-ui, sans-serif">do 24 hodín.</text>
      <text x={60} y={H - 208} fill="#9CB4A0" fontSize="22"
            fontFamily="Inter, system-ui, sans-serif">
        Zadarmo vás spojíme s 3 firmami vo vašom okolí.
      </text>
      <text x={60} y={H - 176} fill="#9CB4A0" fontSize="22"
            fontFamily="Inter, system-ui, sans-serif">Vyberte si najlepšiu.</text>
      <rect x={60} y={H - 130} width={280} height={68} rx="34" fill="#F59E0B" />
      <text x={200} y={H - 86} fill="#0F1A14" fontSize="26" fontWeight="800"
            fontFamily="Inter, system-ui, sans-serif" textAnchor="middle">Získať ponuky</text>
      <text x={360} y={H - 86} fill="#9CB4A0" opacity="0.9" fontSize="18" fontWeight="600"
            fontFamily="Inter, system-ui, sans-serif">stavebne-dopyty.sk</text>
    </Frame>
  );
}

/* ---------- Martinus ---------- */
function BeforeMartinus() {
  return (
    <Frame bg="#FFFFFF">
      {/* Photo-looking shelf */}
      <defs>
        <linearGradient id="shelf" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#CDB28C" />
          <stop offset="1" stopColor="#8B6A45" />
        </linearGradient>
      </defs>
      <rect x="0" y="0" width={W} height={H} fill="url(#shelf)" />
      {/* Fake book spines */}
      {Array.from({ length: 14 }).map((_, i) => (
        <rect key={i} x={30 + i * 82} y={80} width={70} height={430}
              fill={["#8B1A1A", "#284B63", "#1E5128", "#4A1942", "#1E1E1E", "#8A5A12"][i % 6]} />
      ))}
      {/* Price tag */}
      <g transform="translate(980, 220)">
        <polygon points="-100,-80 100,-80 120,0 100,80 -100,80 -120,0" fill="#FFD93D" stroke="#1E1E1E" strokeWidth="4" />
        <text x="0" y="-10" textAnchor="middle" fontFamily="Arial, sans-serif"
              fontSize="22" fontWeight="700" fill="#1E1E1E">Zľava až</text>
        <text x="0" y="45" textAnchor="middle" fontFamily="Arial Black, Arial"
              fontSize="68" fontWeight="900" fill="#B00020">−70%</text>
      </g>
      {/* White content box */}
      <rect x="60" y="60" width="560" height="280" fill="#FFFFFF" opacity="0.94" stroke="#1E1E1E" strokeWidth="2" />
      <text x="340" y="130" textAnchor="middle" fontFamily="Times New Roman, serif"
            fontSize="44" fontWeight="700" fill="#1E1E1E">Kniha za super cenu!</text>
      <text x="340" y="180" textAnchor="middle" fontFamily="Arial" fontSize="20" fill="#1E1E1E">
        Stovky titulov v zľave — beletria, detské,
      </text>
      <text x="340" y="208" textAnchor="middle" fontFamily="Arial" fontSize="20" fill="#1E1E1E">
        odborné knihy, e-knihy a audioknihy.
      </text>
      <text x="340" y="258" textAnchor="middle" fontFamily="Comic Sans MS, cursive"
            fontSize="24" fill="#B00020">Objednajte si už dnes!</text>
      <rect x="230" y="290" width="220" height="40" fill="#B00020" />
      <text x="340" y="317" textAnchor="middle" fontFamily="Arial, sans-serif"
            fontSize="17" fontWeight="700" fill="#FFFFFF">KÚPIŤ SI KNIHU</text>
    </Frame>
  );
}

function AfterMartinus() {
  return (
    <Frame bg="#8B1A1A">
      <g transform="translate(920, 290) rotate(-8) translate(-920, -290)">
        <rect x={780} y={80} width={280} height={380} fill="#FACC15" opacity="0.9" rx="4" />
        <rect x={830} y={120} width={230} height={320} fill="#FFFDF5" opacity="0.95" rx="3" />
        <text x={945} y={270} fill="#8B1A1A" fontSize="34" fontWeight="900"
              fontFamily="Inter, system-ui, sans-serif" textAnchor="middle">BEST</text>
        <text x={945} y={310} fill="#8B1A1A" fontSize="34" fontWeight="900"
              fontFamily="Inter, system-ui, sans-serif" textAnchor="middle">SELLER</text>
      </g>
      <rect x={60} y={64} width={12} height={14} fill="#FACC15" rx="2" />
      <text x={82} y={78} fill="#FACC15" fontSize="18" fontWeight="700"
            fontFamily="Inter, system-ui, sans-serif" letterSpacing="2">KNIHA TÝŽDŇA</text>
      <text x={60} y={200} fill="#FFF8E7" fontSize="76" fontWeight="900"
            fontFamily="Inter, system-ui, sans-serif">Čítaš alebo žiješ?</text>
      <text x={60} y={280} fill="#FFF8E7" fontSize="76" fontWeight="900"
            fontFamily="Inter, system-ui, sans-serif">Obe naraz.</text>
      <text x={60} y={H - 208} fill="#F5C6A0" fontSize="22"
            fontFamily="Inter, system-ui, sans-serif">
        Bestseller týždňa — dnes −30 %.
      </text>
      <text x={60} y={H - 176} fill="#F5C6A0" fontSize="22"
            fontFamily="Inter, system-ui, sans-serif">
        Doprava zdarma nad 25 €.
      </text>
      <rect x={60} y={H - 130} width={260} height={68} rx="34" fill="#FACC15" />
      <text x={190} y={H - 86} fill="#8B1A1A" fontSize="26" fontWeight="800"
            fontFamily="Inter, system-ui, sans-serif" textAnchor="middle">Kúpiť knihu</text>
      <text x={340} y={H - 86} fill="#F5C6A0" opacity="0.9" fontSize="18" fontWeight="600"
            fontFamily="Inter, system-ui, sans-serif">martinus.sk</text>
    </Frame>
  );
}
